import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAccountBilling } from 'hooks/billing/useAccountBilling';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useConversionPopup } from 'hooks/billing/useConversionPopup';
import { useIsApproaching } from 'hooks/billing/useIsApproaching';
import { usePricingConfig } from 'hooks/billing/usePricingConfig';
import { useQuota } from 'hooks/billing/useQuota';
import { useSelfServeGate } from 'hooks/billing/useSelfServeGate';
import { useShowsReversedMigrationOffer } from 'hooks/billing/useShowsReversedMigrationOffer';
import { useIsPlansPageAllowed } from 'hooks/useIsPlansPageAllowed';
import { useUser } from 'hooks/useUser';
import { BellRing, ChevronRight, Gem, Sparkles } from 'lucide-react';
import { ROUTE_PATHS } from 'routes/consts';
import { CONVERSION_POPUP_TRIGGER_SOURCES } from 'services/mixpanel/consts';
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from 'ui/sidebar';
import {
  BILLING_DANGER,
  BILLING_OVERAGE,
  BILLING_SURFACE,
  BILLING_TEXT,
} from 'utils/billing/billingSurface';
import {
  getMigrationDaysRemaining,
  isMigratedPaidUser,
} from 'utils/billing/conversion';
import {
  BILLING_STATUS_CTAS,
  BILLING_STATUS_KEYS,
  deriveBillingStatus,
  type BillingStatusVariant,
} from 'utils/billing/deriveBillingStatus';
import {
  deriveSidebarWidget,
  SIDEBAR_WIDGET_TONES,
  type SidebarWidgetTone,
} from 'utils/billing/deriveSidebarWidget';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { BillingStatusChip } from 'components/Billing/BillingStatusChip';
import { ConversionPopupButton } from 'components/Billing/ConversionPopupButton';
import { getTrialDiscountPct } from 'components/Billing/utils';
import { SIDEBAR_UPGRADE_MODE } from '../consts';
import { getSidebarUpgradeMode } from './getSidebarUpgradeMode';
import { UpgradeNowSkeleton } from './UpgradeNowSkeleton';

const spawnText = spawn('sidebar.footer.upgrade-now');
const spawnPill = spawn('billing.pill');

// Legacy (non-self-serve / enterprise) upgrade button styles.
const LEGACY_UPGRADE_BASE_CLASS =
  'w-[calc(100%-16px)] flex flex-col gap-4 mx-4 p-4 h-fit text-text-general-emphasis-mid';
const LEGACY_UPGRADE_BORDER_CLASS =
  'border dark:border-border-panel-neutral hover:dark:bg-fill-interactive-neutral-idle dark:bg-fill-panel-neutral';
const LEGACY_UPGRADE_ICON_CLASS = '!text-purple-400 stroke-1';

// Card surface per state: trial = purple gradient/outline; overage = its own
// amber/warning treatment (Figma 3397:8984); hard block = red maroon surface.
const SIDEBAR_SURFACE: Record<SidebarWidgetTone, string> = {
  trial: BILLING_SURFACE.trial,
  overage: cn('border bg-transparent', BILLING_OVERAGE.border),
  blocked: BILLING_SURFACE.danger,
};

// Title row (chip icon + label) colour. Trial reads muted grey per Figma;
// overage turns the chip + warning icon amber; blocked/approaching turn it the
// danger pink so the red treatment is consistent across all three surfaces
// (hero chip, top-bar pill, sidebar widget) — matches BillingHero's TONE_CHIP.
const TITLE_COLOR: Record<SidebarWidgetTone, string> = {
  trial: BILLING_TEXT.offWhite,
  overage: BILLING_OVERAGE.text,
  blocked: BILLING_DANGER.text,
};

// Dot separator colour inside BillingStatusChip, mirroring BillingStatusPill's
// VARIANT_CLASSES dot values for the matching severity bands.
const DOT_COLOR: Record<BillingStatusVariant, string> = {
  emphasis: 'bg-border-panel-emphasis',
  warning: 'bg-orange-300',
  danger: BILLING_DANGER.dot,
};

/**
 * Sidebar-bottom billing widget (PLT-4074) — one of the 3 self-serve state
 * surfaces. For self-serve users it renders a state-colored card (trial /
 * soft-overage / block) and nothing on healthy/paid. Non-self-serve users keep
 * the legacy "Upgrade your plan" button (enterprise tree = zero diff).
 */
export const UpgradeNow = () => {
  const { allowed, isLoading: isGateLoading } = useSelfServeGate();
  const { isPlansPageAllowed } = useIsPlansPageAllowed(true);
  const { data: accountInfo } = useAccountInfo();
  const { data: quota, isLoading: isQuotaLoading } = useQuota();
  const { isTrialLapsed } = useAccountBilling();
  const { open: openPopup } = useConversionPopup();
  const cfg = usePricingConfig();
  const { isTrialUser, dayLeftForTrial } = useUser();
  const { state } = useSidebar();
  const navigate = useNavigate();
  // Paid pool nearing its limit → red approaching widget, same as a block.
  const approaching = useIsApproaching();
  // Reversed-but-not-yet-bought within the live coupon window (hook so it's called
  // before the early returns below; folds in the no-subscription stop-signal).
  const showsReversedOffer = useShowsReversedMigrationOffer();

  const collapsed = state === 'collapsed';
  const mode = getSidebarUpgradeMode(allowed, isPlansPageAllowed);

  // Derived locally from already-fetched data — no separate useBillingStatus
  // hook needed (avoids duplicating all the same queries).
  const billingStatus = useMemo(() => {
    if (!quota) return undefined;

    if (isTrialLapsed) {
      return {
        key: BILLING_STATUS_KEYS.trialEnded,
        interpolation: undefined,
        variant: 'danger' as BillingStatusVariant,
        cta: BILLING_STATUS_CTAS.seePlan,
      };
    }

    return deriveBillingStatus(
      quota,
      cfg,
      accountInfo?.trial ?? undefined,
      accountInfo?.tier ?? undefined,
    );
  }, [quota, isTrialLapsed, cfg, accountInfo]);

  if (
    isGateLoading ||
    (mode === SIDEBAR_UPGRADE_MODE.SELF_SERVE && isQuotaLoading)
  ) {
    return <UpgradeNowSkeleton collapsed={collapsed} />;
  }

  // Pre-cancel (awaiting move) AND reversed-but-not-yet-bought users get the same
  // migration countdown widget; the reversed side flips off once the coupon expires
  // or they buy.
  const showsMigrationOffer =
    isMigratedPaidUser(accountInfo) || showsReversedOffer;
  const migrationDaysRemaining = showsMigrationOffer
    ? getMigrationDaysRemaining(accountInfo?.migrationEndDate)
    : undefined;

  if (
    showsMigrationOffer &&
    migrationDaysRemaining !== undefined &&
    migrationDaysRemaining > 0
  ) {
    const daysRemaining = migrationDaysRemaining;
    const isLastDay = daysRemaining <= 1;
    const isEndingSoon = daysRemaining <= cfg.trialEndingThresholdDays;
    const tone: SidebarWidgetTone = isEndingSoon
      ? SIDEBAR_WIDGET_TONES.blocked
      : SIDEBAR_WIDGET_TONES.trial;
    const chipColorClass = TITLE_COLOR[tone];
    const dotColorClass = isEndingSoon ? DOT_COLOR.danger : DOT_COLOR.emphasis;
    const discount = getTrialDiscountPct(accountInfo);
    const months = accountInfo?.migrationPerk?.durationMonths;
    const collapsedLabel = spawnText('migration.title');

    if (collapsed) {
      return (
        <SidebarMenuButton
          onClick={() =>
            openPopup(
              undefined,
              CONVERSION_POPUP_TRIGGER_SOURCES.SIDEBAR_WIDGET,
            )
          }
          tooltip={collapsedLabel}
          aria-label={collapsedLabel}
          className="mx-4 flex h-fit w-[calc(100%-16px)] justify-center border-none p-4"
        >
          <Sparkles className={cn('size-4', chipColorClass)} />
        </SidebarMenuButton>
      );
    }

    return (
      <SidebarMenuItem
        aria-label={collapsedLabel}
        onClick={() =>
          openPopup(undefined, CONVERSION_POPUP_TRIGGER_SOURCES.SIDEBAR_WIDGET)
        }
        className={cn(
          'mx-auto flex h-fit w-[calc(100%-16px)] flex-col items-start gap-4 rounded-md px-4 pt-4 pb-2 text-left text-text-general-emphasis-mid hover:cursor-pointer hover:bg-fill-action-neutral-hover',
          SIDEBAR_SURFACE[tone],
        )}
      >
        <BillingStatusChip colorClass={chipColorClass}>
          {isLastDay ? (
            spawnPill('migration-last-day')
          ) : (
            <Trans
              i18nKey="billing.pill.migration"
              count={daysRemaining}
              components={{
                dot: (
                  <span
                    className={cn(
                      'inline-block size-1 rounded-full',
                      dotColorClass,
                    )}
                  />
                ),
              }}
            />
          )}
        </BillingStatusChip>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-white">
            {discount !== undefined && months !== undefined
              ? spawnText('migration.line2', { discount, months })
              : spawnText('migration.line2-no-perk')}
          </span>
        </div>
        <ConversionPopupButton
          variant="link"
          size="xs"
          icon={ChevronRight}
          iconPosition="right"
          triggerSource={CONVERSION_POPUP_TRIGGER_SOURCES.SIDEBAR_WIDGET}
          className="p-0 text-sm text-action-emphasis-idle [&>svg]:text-inherit"
        >
          {spawnText('migration.cta')}
        </ConversionPopupButton>
      </SidebarMenuItem>
    );
  }

  if (mode === SIDEBAR_UPGRADE_MODE.SELF_SERVE) {
    const vm = deriveSidebarWidget(
      quota,
      accountInfo?.trial ?? undefined,
      approaching,
      isTrialLapsed,
    );

    if (!vm) {
      return null;
    }

    // Collapsed sidebar: show only the tone-colored icon (no card body). The
    // icon-only button gets a tooltip (shown when collapsed) + aria-label so the
    // control is discoverable and screen-reader accessible.
    const collapsedLabel = spawnText(vm.titleKey, vm.interpolation);

    if (collapsed) {
      return (
        <SidebarMenuButton
          onClick={() =>
            openPopup(
              undefined,
              CONVERSION_POPUP_TRIGGER_SOURCES.SIDEBAR_WIDGET,
            )
          }
          tooltip={collapsedLabel}
          aria-label={collapsedLabel}
          className="mx-4 flex h-fit w-[calc(100%-16px)] justify-center border-none p-4"
        >
          <Sparkles className={cn('size-4', TITLE_COLOR[vm.tone])} />
        </SidebarMenuButton>
      );
    }

    // Chip content: use billing.pill.* format ("TRIAL · N DAYS LEFT") when
    // billingStatus is available; fall back to vm.titleKey during initial load
    // when quota hasn't resolved yet.
    const chipContent = billingStatus ? (
      billingStatus.key === BILLING_STATUS_KEYS.trial ? (
        <Trans
          i18nKey={`billing.pill.${billingStatus.key}`}
          count={billingStatus.interpolation?.count as number}
          components={{
            dot: (
              <span
                className={cn(
                  'inline-block size-1 rounded-full',
                  DOT_COLOR[billingStatus.variant],
                )}
              />
            ),
          }}
        />
      ) : (
        spawnPill(billingStatus.key, billingStatus.interpolation)
      )
    ) : (
      spawnText(vm.titleKey, vm.interpolation)
    );

    return (
      <SidebarMenuItem
        aria-label={collapsedLabel}
        onClick={() =>
          openPopup(undefined, CONVERSION_POPUP_TRIGGER_SOURCES.SIDEBAR_WIDGET)
        }
        className={cn(
          'mx-auto flex h-fit w-[calc(100%-16px)] flex-col items-start gap-4 rounded-md px-4 pt-4 pb-2 text-left text-text-general-emphasis-mid hover:cursor-pointer hover:bg-fill-action-neutral-hover',
          SIDEBAR_SURFACE[vm.tone],
        )}
      >
        <BillingStatusChip colorClass={TITLE_COLOR[vm.tone]}>
          {chipContent}
        </BillingStatusChip>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-white">
            {spawnText(vm.line2Key, vm.interpolation)}
          </span>
        </div>
        <span className="flex items-center gap-1 p-0 text-sm text-action-emphasis-idle">
          {spawnText(vm.ctaKey)}
          <ChevronRight className="size-4 text-inherit" />
        </span>
      </SidebarMenuItem>
    );
  }

  if (mode === SIDEBAR_UPGRADE_MODE.HIDDEN) {
    return null;
  }

  return (
    <SidebarMenuButton
      onClick={() => navigate(ROUTE_PATHS.PLANS)}
      className={cn(
        LEGACY_UPGRADE_BASE_CLASS,
        collapsed ? 'border-none' : LEGACY_UPGRADE_BORDER_CLASS,
      )}
    >
      <div className="flex w-full gap-1.5 items-center [&>*:not(:first-child)]:group-data-[collapsible=icon]:hidden">
        <Gem
          className={cn(
            LEGACY_UPGRADE_ICON_CLASS,
            collapsed ? 'size-4' : 'size-[22px]',
          )}
        />
        <span>{spawnText('button')}</span>
        <ChevronRight
          className="text-text-general-neutral-mid ml-auto"
          size={18}
        />
      </div>
      {isTrialUser && (
        <div className="p-1 rounded text-xs flex items-center justify-center gap-2 bg-fill-interactive-neutral-idle w-full text-text-general-neutral-strongest">
          <BellRing size={16} className="text-text-general-neutral-mid" />
          {spawnText('your-trial-ends-in', { days: dayLeftForTrial })}
        </div>
      )}
    </SidebarMenuButton>
  );
};
